<template>
  <v-menu
    content-class="slot-actions-dropdown"
    location="bottom end"
    transition="scale-transition"
  >
    <template #activator="{ props: menuProps }">
      <v-btn
        v-bind="menuProps"
        icon
        variant="outlined"
        size="x-small"
        color="textDark"
        class="slot-actions-trigger"
        @click.stop
      >
        <v-icon icon="$dots-vertical" />
      </v-btn>
    </template>

    <div class="slot-actions-dropdown-inner d-flex flex-column">
      <button
        v-for="approver in remindableApprovers"
        :key="approver.ulid"
        type="button"
        class="slot-action-item"
        :disabled="isRemindingApprover(approver.ulid)"
        @click="handleRemindApprover(approver)"
      >
        Remind {{ getApproverFirstName(approver) }}
      </button>
      <button
        type="button"
        class="slot-action-item"
        @click="handleAddApprovers"
      >
        Add Approvers
      </button>
    </div>
  </v-menu>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useApprovalsDashboardStore } from '@/stores/jobProfiles/approvalsDashboard';

const props = defineProps({
  slotData: {
    type: Object,
    required: true,
  },
  draftJobProfileUlid: {
    type: String,
    default: '',
  },
  flowUlid: {
    type: String,
    required: true,
  },
});

const router = useRouter();
const { remindSlotApprover } = useApprovalsDashboardStore();
const { remindingSlotApproverKey } = storeToRefs(useApprovalsDashboardStore());

const normalizedStatus = computed(() => (
  String(props.slotData?.status || '').toLowerCase()
));

const remindableApprovers = computed(() => {
  if (normalizedStatus.value !== 'pending') {
    return [];
  }

  return (props.slotData?.approvers ?? []).filter((approver) => approver?.ulid);
});

const getApproverFirstName = (approver) => (
  approver?.first_name?.trim() || 'Unknown'
);

const isRemindingApprover = (approverUlid) => (
  remindingSlotApproverKey.value === `${props.slotData?.ulid}:${approverUlid}`
);

const handleRemindApprover = async (approver) => {
  await remindSlotApprover({
    slotUlid: props.slotData.ulid,
    approver,
  });
};

const handleAddApprovers = () => {
  if (!props.draftJobProfileUlid || !props.flowUlid) return;

  router.push({
    name: 'job-profile-view',
    params: { id: props.draftJobProfileUlid },
    query: {
      sidebar: 'approvals',
      approvalFlow: props.flowUlid,
      approvalStep: 'edit',
    },
  });
};
</script>

<style scoped lang="less">
.slot-actions-trigger {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-color: @stroke-default;
  background: @white;
}
</style>

<style lang="less">
.slot-actions-dropdown {
  background: @stroke-default;
  border: 1px solid @stroke-default;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: none;

  .slot-actions-dropdown-inner {
    gap: 1px;
    min-width: 140px;
  }

  .slot-action-item {
    display: block;
    width: 100%;
    border: none;
    background: @white;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.5;
    color: @text-dark;
    cursor: pointer;
    text-align: left;

    &:hover:not(:disabled) {
      background: @bg-canvas;
    }

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }
  }
}
</style>
